import React, { useState, useEffect } from 'react';
import { Card, CardBody, CardHeader, Table, Row, Col, Button, Form, FormGroup, Label, Input, Badge, Modal, ModalHeader, ModalBody, ModalFooter, InputGroup, InputGroupAddon, InputGroupText } from 'reactstrap';
import ApiService from '../services/ApiService';
import { useTranslation } from 'react-i18next';
import PublicIdDisplay from './PublicIdDisplay';
import { useLocation } from 'react-router-dom';

const AdminTVBroadcastManager = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [events, setEvents] = useState([]);
  const [broadcasts, setBroadcasts] = useState([]);
  const [filteredBroadcasts, setFilteredBroadcasts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [editingBroadcast, setEditingBroadcast] = useState(null);
  const [estimateExposures, setEstimateExposures] = useState(false);
  const [exposures, setExposures] = useState([{ logo_class: '', seconds_exposed: '' }]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEventFilter, setSelectedEventFilter] = useState('');
  const [formData, setFormData] = useState({
    event_id: '',
    starts_at: '',
    ends_at: '',
    duration: '',
    source: '',
    channel: '',
    identifier: '',
    country: '',
    title: '',
    media_url: '',
    media_type: 'stream'
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    // Check if an event was passed via location state
    if (location.state?.eventId) {
      setSelectedEventFilter(location.state.eventId);
    }
  }, [location]);

  useEffect(() => {
    // Filter broadcasts based on search term and selected event
    let filtered = [...broadcasts];
    
    if (searchTerm) {
      filtered = filtered.filter(broadcast => 
        broadcast.public_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        broadcast.title?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (selectedEventFilter) {
      filtered = filtered.filter(broadcast => {
        // Check if broadcast has an event with matching public_id
        if (broadcast.event && broadcast.event.public_id) {
          return broadcast.event.public_id === selectedEventFilter;
        }
        // If no event is linked, don't include in filtered results
        return false;
      });
    }
    
    setFilteredBroadcasts(filtered);
  }, [broadcasts, searchTerm, selectedEventFilter]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [eventsRes, broadcastsRes] = await Promise.all([
        ApiService.getAdminEvents({ per_page: 100 }), // Get more events for the dropdown
        ApiService.getTvBroadcasts()
      ]);
      // Handle pagination response from getAdminEvents and sort by title
      const eventsList = eventsRes.data.data || eventsRes.data;
      const sortedEvents = [...eventsList].sort((a, b) => 
        (a.title || '').localeCompare(b.title || '')
      );
      setEvents(sortedEvents);
      setBroadcasts(broadcastsRes.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching data:", error);
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCreateBroadcast = () => {
    setEditingBroadcast(null);
    setEstimateExposures(false);
    setExposures([{ logo_class: '', seconds_exposed: '' }]);
    setFormData({
      event_id: '',
      starts_at: '',
      ends_at: '',
      duration: '',
      source: '',
      channel: '',
      identifier: '',
      country: '',
      title: '',
      media_url: '',
      media_type: 'stream'
    });
    setModal(true);
  };

  const handleEdit = (broadcast) => {
    setEditingBroadcast(broadcast);
    setFormData({
      event_id: broadcast.event?.public_id || '',
      starts_at: broadcast.starts_at ? new Date(broadcast.starts_at).toISOString().slice(0, 16) : '',
      ends_at: broadcast.ends_at ? new Date(broadcast.ends_at).toISOString().slice(0, 16) : '',
      duration: broadcast.duration || '',
      source: broadcast.source || '',
      channel: broadcast.channel || '',
      identifier: broadcast.identifier || '',
      country: broadcast.country || '',
      title: broadcast.title || '',
      media_url: broadcast.media_url || '',
      media_type: broadcast.media_type || 'stream'
    });
    // Load existing exposures if any
    setExposures(broadcast.estimated_exposures && broadcast.estimated_exposures.length > 0 
      ? broadcast.estimated_exposures.map(exp => ({ logo_class: exp.logo_class, seconds_exposed: exp.seconds_exposed }))
      : [{ logo_class: '', seconds_exposed: '' }]
    );
    setEstimateExposures(broadcast.estimated_exposures && broadcast.estimated_exposures.length > 0);
    setModal(true);
  };

  const handleCloseModal = () => {
    setModal(false);
    setEditingBroadcast(null);
    setEstimateExposures(false);
    setExposures([{ logo_class: '', seconds_exposed: '' }]);
    setFormData({
      event_id: '',
      starts_at: '',
      ends_at: '',
      duration: '',
      source: '',
      channel: '',
      identifier: '',
      country: '',
      title: '',
      media_url: '',
      media_type: 'stream'
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      if (editingBroadcast) {
        await ApiService.updateTvBroadcast(editingBroadcast.public_id, formData);
        if (estimateExposures && exposures.some(exp => exp.logo_class && exp.seconds_exposed)) {
          await ApiService.saveBroadcastExposures(editingBroadcast.public_id, exposures.filter(exp => exp.logo_class && exp.seconds_exposed));
        }
      } else {
        const response = await ApiService.createTvBroadcast(formData);
        if (estimateExposures && exposures.some(exp => exp.logo_class && exp.seconds_exposed)) {
          await ApiService.saveBroadcastExposures(response.data.public_id, exposures.filter(exp => exp.logo_class && exp.seconds_exposed));
        }
      }
      fetchData();
      handleCloseModal();
    } catch (error) {
      console.error("Error saving broadcast:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleExposureChange = (index, field, value) => {
    const newExposures = [...exposures];
    newExposures[index][field] = value;
    setExposures(newExposures);
  };

  const addExposure = () => {
    setExposures([...exposures, { logo_class: '', seconds_exposed: '' }]);
  };

  const removeExposure = (index) => {
    if (exposures.length > 1) {
      setExposures(exposures.filter((_, i) => i !== index));
    }
  };

  return (
    <div className="container-fluid">
      <Row className="mb-4">
        <Col xs={12}>
          <Card className="admin-card">
            <CardHeader className="admin-card-header d-flex justify-content-between align-items-center">
              <h4 className="admin-card-title">TV Broadcast Manager</h4>
              <Button color="primary" onClick={handleCreateBroadcast}>
                <i className="fas fa-plus mr-2"></i>Create Broadcast
              </Button>
            </CardHeader>
            <CardBody>
              <Row className="mb-3">
                <Col md={4}>
                  <InputGroup>
                    <InputGroupAddon addonType="prepend">
                      <InputGroupText><i className="fas fa-search"></i></InputGroupText>
                    </InputGroupAddon>
                    <Input
                      type="text"
                      placeholder="Search by ID or title..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </InputGroup>
                </Col>
                <Col md={4}>
                  <Input
                    type="select"
                    value={selectedEventFilter}
                    onChange={(e) => setSelectedEventFilter(e.target.value)}
                  >
                    <option value="">All Events</option>
                    {events.map(event => (
                      <option key={event.public_id} value={event.public_id}>
                        {event.title} - {event.category}{event.class ? ` (${event.class})` : ''}
                      </option>
                    ))}
                  </Input>
                </Col>
                <Col md={4} className="text-right">
                  {(searchTerm || selectedEventFilter) && (
                    <Button 
                      color="link" 
                      onClick={() => {
                        setSearchTerm('');
                        setSelectedEventFilter('');
                      }}
                    >
                      Clear Filters
                    </Button>
                  )}
                </Col>
              </Row>
              <Table responsive hover>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Title</th>
                    <th>Event</th>
                    <th>Channel</th>
                    <th>Start Time</th>
                    <th>Duration</th>
                    <th>Estimated Exposures</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredBroadcasts.length === 0 && (searchTerm || selectedEventFilter) ? (
                    <tr>
                      <td colSpan="8" className="text-center">No broadcasts found matching your filters</td>
                    </tr>
                  ) : filteredBroadcasts.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="text-center">No broadcasts found</td>
                    </tr>
                  ) : (
                    filteredBroadcasts.map(broadcast => (
                    <tr key={broadcast.public_id}>
                      <td><PublicIdDisplay publicId={broadcast.public_id} /></td>
                      <td>{broadcast.title}</td>
                      <td>{broadcast.event?.title}</td>
                      <td>{broadcast.channel}</td>
                      <td>{new Date(broadcast.starts_at).toLocaleString()}</td>
                      <td>{broadcast.duration} min</td>
                      <td>
                        {broadcast.estimated_exposures && broadcast.estimated_exposures.length > 0 ? (
                          <Badge color="success">{broadcast.estimated_exposures.length} classes</Badge>
                        ) : (
                          <Badge color="secondary">None</Badge>
                        )}
                      </td>
                      <td>
                        <Button
                          color="secondary"
                          outline
                          size="sm"
                          onClick={() => handleEdit(broadcast)}
                          title="Edit Broadcast"
                        >
                          <i className="fas fa-edit"></i>
                        </Button>
                      </td>
                    </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Create/Edit Modal */}
      <Modal isOpen={modal} toggle={handleCloseModal} size="lg">
        <Form onSubmit={handleSubmit}>
          <ModalHeader toggle={handleCloseModal}>
            {editingBroadcast ? 'Edit TV Broadcast' : 'Create TV Broadcast'}
          </ModalHeader>
          <ModalBody>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>Event</Label>
                  <Input
                    type="select"
                    name="event_id"
                    value={formData.event_id}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select Event</option>
                    {events.map(event => (
                      <option key={event.public_id} value={event.public_id}>
                        {event.title} - {event.category}{event.class ? ` (${event.class})` : ''}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Title</Label>
                  <Input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    required
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={4}>
                <FormGroup>
                  <Label>Start Time</Label>
                  <Input
                    type="datetime-local"
                    name="starts_at"
                    value={formData.starts_at}
                    onChange={handleInputChange}
                    required
                  />
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label>End Time</Label>
                  <Input
                    type="datetime-local"
                    name="ends_at"
                    value={formData.ends_at}
                    onChange={handleInputChange}
                    required
                  />
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label>Duration (minutes)</Label>
                  <Input
                    type="number"
                    name="duration"
                    value={formData.duration}
                    onChange={handleInputChange}
                    required
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={4}>
                <FormGroup>
                  <Label>Source</Label>
                  <Input
                    type="text"
                    name="source"
                    value={formData.source}
                    onChange={handleInputChange}
                    required
                  />
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label>Channel</Label>
                  <Input
                    type="text"
                    name="channel"
                    value={formData.channel}
                    onChange={handleInputChange}
                    required
                  />
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label>Country</Label>
                  <Input
                    type="text"
                    name="country"
                    value={formData.country}
                    onChange={handleInputChange}
                    required
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>Media URL (Optional)</Label>
                  <Input
                    type="text"
                    name="media_url"
                    value={formData.media_url}
                    onChange={handleInputChange}
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Media Type</Label>
                  <Input
                    type="select"
                    name="media_type"
                    value={formData.media_type}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="stream">Stream</option>
                    <option value="file">File</option>
                  </Input>
                </FormGroup>
              </Col>
            </Row>
            
            {/* Estimate Exposures Toggle */}
            <Row className="mt-4">
              <Col md={12}>
                <FormGroup check>
                  <Input
                    type="checkbox"
                    id="estimateExposures"
                    checked={estimateExposures}
                    onChange={(e) => setEstimateExposures(e.target.checked)}
                  />
                  <Label check for="estimateExposures" className="ml-2">
                    <strong>Estimate Exposures</strong>
                  </Label>
                </FormGroup>
              </Col>
            </Row>

            {/* Exposures Form */}
            {estimateExposures && (
              <div className="mt-3 p-3 border rounded">
                <h6 className="mb-3">Logo Class Exposures</h6>
                {exposures.map((exposure, index) => (
                  <Row key={index} className="mb-2">
                    <Col md={5}>
                      <Input
                        type="text"
                        placeholder="Logo Class"
                        value={exposure.logo_class}
                        onChange={(e) => handleExposureChange(index, 'logo_class', e.target.value)}
                      />
                    </Col>
                    <Col md={4}>
                      <Input
                        type="number"
                        placeholder="Seconds"
                        value={exposure.seconds_exposed}
                        onChange={(e) => handleExposureChange(index, 'seconds_exposed', e.target.value)}
                      />
                    </Col>
                    <Col md={3}>
                      {exposures.length > 1 && (
                        <Button
                          color="danger"
                          size="sm"
                          onClick={() => removeExposure(index)}
                        >
                          Remove
                        </Button>
                      )}
                      {index === exposures.length - 1 && (
                        <Button
                          color="success"
                          size="sm"
                          className="ml-2"
                          onClick={addExposure}
                        >
                          Add
                        </Button>
                      )}
                    </Col>
                  </Row>
                ))}
              </div>
            )}
          </ModalBody>
          <ModalFooter className="d-flex justify-content-between">
            <Button type="button" color="secondary" onClick={handleCloseModal} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" color="primary" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : (editingBroadcast ? 'Update Broadcast' : 'Create Broadcast')}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>
    </div>
  );
};

export default AdminTVBroadcastManager; 